import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { parseTrQuantity } from '../../../utils/numberFormat';
import GuideButton from '../../common/guide/GuideButton';
import { useProductionPermissions } from '../context/ProductionPermissionsContext';
import {
  ProductionApiError,
  bulkCreateFixedOrders,
  getWarehouses,
  updateFixedOrder,
} from '../services/productionService';
import type {
  BulkCreateFixedOrderResult,
  ProductionFixedOrder,
  ProductionWarehouse,
} from '../types/production.types';
import { formatQty } from '../utils/productionFormat';

/** Reçete listesinden yeni tanım açılırken gelen bilgiler. */
export interface FixedOrderSource {
  productId: string;
  recipeId?: string | null;
  productName: string;
  outputQuantity: number;
  outputUnit: string;
}

interface FixedOrderFormDialogProps {
  open: boolean;
  /** Düzenlenen tanım; null ise yeni tanım (çok şubeli). */
  initial: ProductionFixedOrder | null;
  source?: FixedOrderSource | null;
  onClose: () => void;
  /** Düzenlemede kaydedilen tanım; yeni tanımda null ve bilgi mesajı. */
  onSaved: (saved: ProductionFixedOrder | null, message?: string) => void;
}

/** "Tüm şubeler" seçeneğinin anahtarı. */
const ALL_KEY = '__all__';
/** Şubesiz depolar için grup anahtarı. */
const NO_BRANCH_KEY = '';
/** Sunucudaki toplu tanım sınırı. */
const MAX_BULK_ITEMS = 200;

/** Deposu olan bir şube ve depoları (ada göre sıralı). */
interface BranchGroup {
  key: string;
  name: string;
  warehouses: ProductionWarehouse[];
}

/** Seçilen şube için girdi/çıktı deposu satırı. */
interface BranchRow {
  key: string;
  inputId: string;
  outputId: string;
}

/** Varsayılan depo: şubenin varsayılan deposu, yoksa tek / ilk (alfabetik) depo. */
function defaultWarehouseId(group: BranchGroup): string {
  return group.warehouses.find((warehouse) => warehouse.isDefault)?.id || group.warehouses[0]?.id || '';
}

function newRow(group: BranchGroup): BranchRow {
  const id = defaultWarehouseId(group);
  return { key: group.key, inputId: id, outputId: id };
}

/** Atlanan şubelerin adları: sunucudaki şube adı, yoksa yerel şube/depo adı. */
function skippedNames(
  result: BulkCreateFixedOrderResult,
  groups: BranchGroup[],
  warehouses: ProductionWarehouse[],
): string {
  return result.skipped
    .map((row) => {
      if (row.branchName) return row.branchName;
      const warehouse = warehouses.find((item) => item.id === row.outputWarehouseId);
      const group = groups.find((item) => item.key === (warehouse?.branchId || NO_BRANCH_KEY));
      return group?.name || warehouse?.name || '';
    })
    .filter((name, index, list) => Boolean(name) && list.indexOf(name) === index)
    .join(', ');
}

const FixedOrderFormDialog: React.FC<FixedOrderFormDialogProps> = ({ open, initial, source, onClose, onSaved }) => {
  const { t } = useTranslation('production');
  const { branches } = useAuth();
  const perms = useProductionPermissions();
  const [warehouses, setWarehouses] = useState<ProductionWarehouse[]>([]);
  const [loadingWarehouses, setLoadingWarehouses] = useState(false);
  const [name, setName] = useState('');
  const [inputWarehouseId, setInputWarehouseId] = useState('');
  const [outputWarehouseId, setOutputWarehouseId] = useState('');
  const [separateOutput, setSeparateOutput] = useState(false);
  const [quantityText, setQuantityText] = useState('');
  const [notes, setNotes] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [rows, setRows] = useState<BranchRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const productName = initial?.productName || source?.productName || '';
  const unit = initial?.unit || source?.outputUnit || '';

  useEffect(() => {
    if (!open) return;
    setName(initial?.name ?? '');
    setNotes(initial?.notes ?? '');
    setIsActive(initial?.isActive ?? true);
    const qty = initial?.quantity ?? source?.outputQuantity ?? 0;
    setQuantityText(qty > 0 ? formatQty(qty) : '');
    setInputWarehouseId(initial?.inputWarehouseId ?? '');
    setOutputWarehouseId(initial?.outputWarehouseId ?? '');
    setSeparateOutput(Boolean(initial && initial.inputWarehouseId !== initial.outputWarehouseId));
    setRows([]);
    setError(null);
  }, [open, initial, source]);

  useEffect(() => {
    if (!open) return undefined;
    let active = true;
    setLoadingWarehouses(true);
    // Liste sunucuda süzülür: izinli depolar (yöneticide kendi şubesi), akıllı depolar hariç.
    getWarehouses()
      .then((list) => {
        if (active) setWarehouses(list);
      })
      .catch((err) => {
        if (active) setError(err instanceof ProductionApiError ? err.message : t('fixedOrders.loadWarehousesFailed'));
      })
      .finally(() => {
        if (active) setLoadingWarehouses(false);
      });
    return () => {
      active = false;
    };
  }, [open, t]);

  const effectiveOutputId = separateOutput ? outputWarehouseId : inputWarehouseId;

  // Şube, fişteki gibi çıktı deposundan türetilir (düzenleme).
  const branchLabel = useMemo(() => {
    const warehouse = warehouses.find((item) => item.id === effectiveOutputId);
    const branchId = warehouse?.branchId;
    if (!branchId) return t('fixedOrders.noBranch');
    return branches?.find((branch) => branch.id === branchId)?.name || initial?.branchName || branchId;
  }, [warehouses, effectiveOutputId, branches, initial, t]);

  // Yalnız en az bir deposu olan şubeler listelenir.
  const groups = useMemo<BranchGroup[]>(() => {
    const map = new Map<string, ProductionWarehouse[]>();
    warehouses.forEach((warehouse) => {
      const key = warehouse.branchId || NO_BRANCH_KEY;
      map.set(key, [...(map.get(key) ?? []), warehouse]);
    });
    return Array.from(map.entries())
      .map(([key, list]) => ({
        key,
        name: key
          ? branches?.find((branch) => branch.id === key)?.name || key
          : t('fixedOrders.noBranch'),
        warehouses: [...list].sort((a, b) => a.name.localeCompare(b.name, 'tr')),
      }))
      .sort((a, b) => {
        if (a.key === NO_BRANCH_KEY) return 1;
        if (b.key === NO_BRANCH_KEY) return -1;
        return a.name.localeCompare(b.name, 'tr');
      });
  }, [warehouses, branches, t]);

  const groupByKey = useMemo(() => new Map(groups.map((group) => [group.key, group])), [groups]);
  const allSelected = groups.length > 0 && rows.length === groups.length;

  // Yeni tanımda yöneticinin şubesi ya da tek şube seçili gelir.
  useEffect(() => {
    if (!open || initial || loadingWarehouses || groups.length === 0) return;
    setRows((current) => {
      if (current.length > 0) return current;
      const preferred = (perms.branchId ? groupByKey.get(perms.branchId) : undefined)
        || (groups.length === 1 ? groups[0] : undefined);
      return preferred ? [newRow(preferred)] : current;
    });
  }, [open, initial, loadingWarehouses, groups, groupByKey, perms.branchId]);

  const handleBranchesChange = (keys: string[], clicked?: string) => {
    if (clicked === ALL_KEY) {
      // Tümü seçiliyken tekrar tıklanırsa temizlenir.
      setRows(allSelected ? [] : groups.map((group) => rows.find((row) => row.key === group.key) ?? newRow(group)));
      return;
    }
    setRows(keys
      .filter((key) => key !== ALL_KEY)
      .map((key) => {
        const existing = rows.find((row) => row.key === key);
        if (existing) return existing;
        const group = groupByKey.get(key);
        return group ? newRow(group) : null;
      })
      .filter((row): row is BranchRow => row !== null));
  };

  const updateRow = (key: string, patch: Partial<BranchRow>) => {
    setRows((current) => current.map((row) => {
      if (row.key !== key) return row;
      // Çıktı girdiyle aynıysa girdi değişince o da değişir.
      if (patch.inputId !== undefined && row.outputId === row.inputId) {
        return { ...row, ...patch, outputId: patch.inputId };
      }
      return { ...row, ...patch };
    }));
  };

  const warehouseOptions = (selectedId: string, fallbackName?: string | null) => (
    [
      ...(selectedId && !warehouses.some((item) => item.id === selectedId)
        ? [<MenuItem key={selectedId} value={selectedId}>{fallbackName || selectedId}</MenuItem>]
        : []),
      ...warehouses.map((warehouse) => (
        <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
      )),
    ]
  );

  const rowWarehouseOptions = (group: BranchGroup | undefined) => (group?.warehouses ?? []).map((warehouse) => (
    <MenuItem key={warehouse.id} value={warehouse.id}>{warehouse.name}</MenuItem>
  ));

  /** Yeni tanım: seçilen her şube için tek istekle kayıt; zaten tanımlı olanlar atlanır. */
  const handleBulkSave = async (productId: string, quantity: number) => {
    if (rows.length === 0) {
      setError(t('fixedOrders.bulk.selectBranch'));
      return;
    }
    if (rows.length > MAX_BULK_ITEMS) {
      setError(t('fixedOrders.bulk.tooMany', { max: MAX_BULK_ITEMS }));
      return;
    }
    if (rows.some((row) => !row.inputId || !row.outputId)) {
      setError(t('fixedOrders.validation.warehouse'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const result = await bulkCreateFixedOrders({
        ProductId: productId,
        RecipeId: source?.recipeId ?? null,
        Name: name.trim() || null,
        Quantity: quantity,
        Notes: notes.trim() || null,
        IsActive: isActive,
        SortOrder: 0,
        Items: rows.map((row) => ({ InputWarehouseId: row.inputId, OutputWarehouseId: row.outputId })),
      });
      const created = result.created.length;
      const skipped = result.skipped.length;
      const names = skippedNames(result, groups, warehouses);
      let message: string;
      if (skipped === 0) message = t('fixedOrders.bulk.created', { count: created });
      else if (created === 0) message = t('fixedOrders.bulk.allSkipped', { names });
      else message = t('fixedOrders.bulk.createdSkipped', { created, skipped, names });
      onSaved(null, message);
      onClose();
    } catch (err) {
      setError(err instanceof ProductionApiError ? err.message : t('fixedOrders.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    const quantity = parseTrQuantity(quantityText);
    if (!Number.isFinite(quantity) || quantity <= 0) {
      setError(t('fixedOrders.validation.quantity'));
      return;
    }
    const productId = initial?.productId || source?.productId;
    if (!productId) return;
    if (!initial) {
      await handleBulkSave(productId, quantity);
      return;
    }
    if (!inputWarehouseId || (separateOutput && !outputWarehouseId)) {
      setError(t('fixedOrders.validation.warehouse'));
      return;
    }

    setSaving(true);
    setError(null);
    const body = {
      ProductId: productId,
      RecipeId: initial.recipeId ?? null,
      Name: name.trim() || null,
      InputWarehouseId: inputWarehouseId,
      OutputWarehouseId: effectiveOutputId,
      Quantity: quantity,
      Notes: notes.trim() || null,
      IsActive: isActive,
      SortOrder: initial.sortOrder ?? 0,
    };
    try {
      const saved = await updateFixedOrder(initial.id, body);
      onSaved(saved);
      onClose();
    } catch (err) {
      setError(err instanceof ProductionApiError ? err.message : t('fixedOrders.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const optionLabel = (key: string) => (key === ALL_KEY ? t('fixedOrders.allBranches') : groupByKey.get(key)?.name ?? key);

  const branchPicker = (
    <>
      <Autocomplete
        multiple
        disableCloseOnSelect
        limitTags={4}
        options={groups.length > 0 ? [ALL_KEY, ...groups.map((group) => group.key)] : []}
        value={rows.map((row) => row.key)}
        onChange={(_, value, __, details) => handleBranchesChange(value, details?.option)}
        getOptionLabel={optionLabel}
        isOptionEqualToValue={(option, value) => option === value}
        loading={loadingWarehouses}
        noOptionsText={t('fixedOrders.bulk.noBranches')}
        renderOption={(props, key, { selected }) => (
          <li {...props} key={key || 'no-branch'}>
            <Checkbox
              size="small"
              sx={{ mr: 1, p: 0.5 }}
              checked={key === ALL_KEY ? allSelected : selected}
              indeterminate={key === ALL_KEY && rows.length > 0 && !allSelected}
            />
            <Typography variant="body2" fontWeight={key === ALL_KEY ? 'medium' : undefined}>
              {optionLabel(key)}
            </Typography>
          </li>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t('fixedOrders.bulk.branches')}
            placeholder={rows.length === 0 ? t('fixedOrders.bulk.searchBranch') : undefined}
            helperText={t('fixedOrders.bulk.branchesHelp')}
          />
        )}
      />
      {rows.length > 0 && (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, maxHeight: 280, overflowY: 'auto' }}>
          {rows.map((row, index) => {
            const group = groupByKey.get(row.key);
            return (
              <Box
                key={row.key || 'no-branch'}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr 1fr auto', sm: 'minmax(110px, 0.9fr) 1fr 1fr auto' },
                  gap: 1,
                  alignItems: 'center',
                  px: 1.5,
                  py: 1,
                  borderTop: index === 0 ? 0 : 1,
                  borderColor: 'divider',
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight="medium"
                  noWrap
                  title={group?.name}
                  sx={{ gridColumn: { xs: '1 / -1', sm: 'auto' } }}
                >
                  {group?.name ?? row.key}
                </Typography>
                <TextField
                  select
                  size="small"
                  label={t('fixedOrders.inputWarehouse')}
                  value={row.inputId}
                  onChange={(event) => updateRow(row.key, { inputId: event.target.value })}
                >
                  {rowWarehouseOptions(group)}
                </TextField>
                <TextField
                  select
                  size="small"
                  label={t('fixedOrders.outputWarehouse')}
                  value={row.outputId}
                  onChange={(event) => updateRow(row.key, { outputId: event.target.value })}
                >
                  {rowWarehouseOptions(group)}
                </TextField>
                <IconButton
                  size="small"
                  aria-label={t('fixedOrders.bulk.removeRow')}
                  title={t('fixedOrders.bulk.removeRow')}
                  onClick={() => setRows((current) => current.filter((item) => item.key !== row.key))}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            );
          })}
        </Box>
      )}
    </>
  );

  const singleWarehouseFields = (
    <>
      <TextField
        select
        label={t('fixedOrders.inputWarehouse')}
        value={inputWarehouseId}
        onChange={(event) => setInputWarehouseId(event.target.value)}
        disabled={loadingWarehouses}
        InputProps={loadingWarehouses ? { endAdornment: <CircularProgress size={16} sx={{ mr: 3 }} /> } : undefined}
      >
        {warehouseOptions(inputWarehouseId, initial?.inputWarehouseName)}
      </TextField>
      <FormControlLabel
        control={(
          <Switch
            checked={separateOutput}
            onChange={(_, checked) => {
              setSeparateOutput(checked);
              if (checked && !outputWarehouseId) setOutputWarehouseId(inputWarehouseId);
            }}
          />
        )}
        label={t('fixedOrders.separateOutput')}
      />
      {separateOutput && (
        <TextField
          select
          label={t('fixedOrders.outputWarehouse')}
          value={outputWarehouseId}
          onChange={(event) => setOutputWarehouseId(event.target.value)}
          disabled={loadingWarehouses}
        >
          {warehouseOptions(outputWarehouseId, initial?.outputWarehouseName)}
        </TextField>
      )}
      <TextField
        label={t('fixedOrders.branch')}
        value={branchLabel}
        InputProps={{ readOnly: true }}
        helperText={t('fixedOrders.branchHelp')}
      />
    </>
  );

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth={initial ? 'sm' : 'md'} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span style={{ flex: 1 }}>{initial ? t('fixedOrders.editTitle') : t('fixedOrders.createTitle')}</span>
        <GuideButton guideKey="production.fixedOrderForm" size="small" />
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pt: 1 }}>
        {error && <Alert severity="error">{error}</Alert>}
        <Box>
          <Typography variant="caption" color="text.secondary">{t('fixedOrders.product')}</Typography>
          <Typography variant="body1" fontWeight="medium">{productName || '—'}</Typography>
        </Box>
        <TextField
          label={t('fixedOrders.name')}
          value={name}
          onChange={(event) => setName(event.target.value)}
          inputProps={{ maxLength: 200 }}
          helperText={t('fixedOrders.nameHelp')}
        />
        {initial ? singleWarehouseFields : branchPicker}
        <TextField
          label={t('fixedOrders.quantity')}
          value={quantityText}
          onChange={(event) => setQuantityText(event.target.value)}
          InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined}
          helperText={source && !initial
            ? t('fixedOrders.quantityHelpRecipe', { value: formatQty(source.outputQuantity), unit: source.outputUnit })
            : t('fixedOrders.quantityHelp')}
          inputProps={{ inputMode: 'decimal' }}
        />
        <TextField
          label={t('fixedOrders.notes')}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          inputProps={{ maxLength: 1000 }}
          multiline
          minRows={2}
        />
        <FormControlLabel
          control={<Switch checked={isActive} onChange={(_, checked) => setIsActive(checked)} />}
          label={isActive ? t('fixedOrders.active') : t('fixedOrders.inactive')}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>{t('fixedOrders.cancel')}</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving || loadingWarehouses}>
          {saving ? <CircularProgress size={18} color="inherit" /> : t('fixedOrders.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FixedOrderFormDialog;
